/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useState } from 'react'
import Image from 'next/image'
import { CardNumberElement, CardMonthElement, CardYearElement, CardCvvElement, useRecurly } from '@recurly/react-recurly'
import { Collapse, userAgent, FormGroup } from '@theneatcompany/nui'
import { useFormContext } from 'react-hook-form'

import { sendError } from '@/utils/rollbar'
import { Columns, Column, Rows } from '@/components/layout'
import creditCardsImg from '@/assets/buy-flow/credit-cards.png'
import paypalBuyImg from '@/assets/buy-flow/pp-logo-200px.png'
import { PAYMENT_METHOD_TYPES, useSignUp } from '@/components/Providers/SignUpProvider'

import styles from './SignUp.module.scss'

const recurlyFieldInitial = {
  number: true,
  month: true,
  year: true,
  cvv: true,
  postal_code: true,
  first_name: true,
  last_name: true,
}

const errorLabels = {
  number: 'Card Number',
  month: 'Month',
  year: 'Year',
  cvv: 'CVV',
  postal_code: 'Postal Code',
  first_name: 'First Name',
  last_name: 'Last Name',
}

const PaymentMethods = () => {
  const inputFontSize = userAgent.isMobile ? '16px' : '14px' // to prevent auto-zoom

  const [isCcOpen, setIsCcOpen] = useState(false)

  const {
    recurlyFormValidation,
    setRecurlyFormValidation,
    isOrderProcessing,
    billingData,
    setRecurlyToken,
    recurlyErrors,
    setRecurlyErrors,
    setIsOrderProcessing,
    setIsPayPalOpen,
    isPayPalOpen,
  } = useSignUp()
  const { watch, register } = useFormContext()

  const [paymentMethod] = watch(['paymentMethod'])

  const recurly = useRecurly()

  const paypal = recurly.PayPal({
    payPalComplete: true,
  })

  paypal.on('error', (err) => {
    sendError(err, 'Error during PayPal login')
    setRecurlyErrors('Error getting paypal')
    setIsPayPalOpen(false)
    setIsOrderProcessing(false)
  })

  paypal.on('cancel', () => {
    setIsOrderProcessing(false)
    setIsPayPalOpen(false)
  })

  paypal.on('token', async (token) => {
    setRecurlyToken(token.id)
    setIsPayPalOpen(false)
  })

  const handleRecurlyErrors = (errors) => {
    const current = { ...recurlyFieldInitial }
    const errorPrefix = 'Uh Oh, it looks like we need some more information. Complete the required fields to continue: '
    const errorMessages = errors?.details?.map(error => {
      const label = errorLabels[error.field]
      if (label) {
        current[error.field] = false
        return label
      }

      return ''
    })

    setRecurlyFormValidation(current)
    setRecurlyErrors(errors.code === 'invalid-parameter' && errors.message
      ? errors.message
      : `${errorPrefix} ${errorMessages?.join(', ')}`)
  }

  const getRecurlyCcToken = async () => {
    const recurlyToken = await new Promise((resolve, reject) => {
      recurly.token({
        first_name: billingData.billingFirstName,
        last_name: billingData.billingLastName,
        country: billingData.billingCountry,
        postal_code: billingData.billingZipCode,
      }, async (err, token) => {
        if (err) {
          handleRecurlyErrors(err)
          reject(null)
        } else {
          setRecurlyFormValidation({ ...recurlyFieldInitial })
          setRecurlyErrors(null)
          resolve(token.id)
        }
      })
    })

    setRecurlyToken(recurlyToken)
  }

  useEffect(() => {
    setRecurlyFormValidation({ ...recurlyFieldInitial })
  }, [])

  useEffect(() => {
    if (paymentMethod === PAYMENT_METHOD_TYPES.ccPayment) {
      setIsCcOpen(true)
    } else {
      setIsCcOpen(false)
    }
  }, [paymentMethod])

  useEffect(() => {
    if (isOrderProcessing && billingData.billingZipCode) {
      if (billingData.paymentMethod === PAYMENT_METHOD_TYPES.ccPayment) {
        getRecurlyCcToken()
      } else if (billingData.paymentMethod === PAYMENT_METHOD_TYPES.paypalPayment && !isPayPalOpen) {
        paypal.start()
        setIsPayPalOpen(true)
      }
    }
  }, [isOrderProcessing, billingData])

  return (
    <>
      <div className={styles.paymentMethodButtons}>
        <label className={styles.paymentLabel}>
          <div className={styles.paymentRadioButton} id={PAYMENT_METHOD_TYPES.ccPayment}>
            <input {...register('paymentMethod')} data-testid="billing-cc-button" id={PAYMENT_METHOD_TYPES.ccPayment} name="paymentMethod" type="radio" value={PAYMENT_METHOD_TYPES.ccPayment} />
            Credit Card
          </div>
        </label>
        <label className={styles.paymentLabel}>
          <div className={styles.paymentRadioButton} id={PAYMENT_METHOD_TYPES.paypalPayment}>
            <input {...register('paymentMethod')} data-testid="billing-paypal-button" id={PAYMENT_METHOD_TYPES.paypalPayment} name="paymentMethod" type="radio" value={PAYMENT_METHOD_TYPES.paypalPayment} />
            <Image alt="paypal" height="auto" src={paypalBuyImg} width={80} />
          </div>
        </label>
      </div>
      <Collapse className={isCcOpen ? styles.ccForm : ''} isOpen={isCcOpen}>
        <Image alt="Neat Credit Cards" className="image-center" src={creditCardsImg} />
        <Column className={styles.billingCardNumber}>
          <CardNumberElement
            className={!recurlyFormValidation.number ? 'recurly-element-invalid' : undefined}
            style={{ placeholder: { content: 'Card Number', color: '#CFCFDF', fontSize: inputFontSize }, fontSize: inputFontSize }}
          />
        </Column>
        <Rows gap={0}>
          <Columns gap={8}>
            <Column>
              <CardMonthElement
                className={!recurlyFormValidation.month ? 'recurly-element-invalid' : undefined}
                inputType="select"
                style={{
                  placeholder: { content: 'MM', color: '#515eda', fontSize: '14px' },
                  fontSize: '14px',
                  fontColor: '#515eda',
                  fontWeight: 700,
                }}
              />
            </Column>
            <Column>
              <CardYearElement
                className={!recurlyFormValidation.year ? 'recurly-element-invalid' : undefined}
                inputType="select"
                style={{
                  placeholder: { content: 'YY', color: '#515eda', fontSize: '14px' },
                  fontSize: '14px',
                  fontColor: '#515eda',
                  fontWeight: 700,
                }}
              />
            </Column>
            <Column>
              <CardCvvElement
                className={!recurlyFormValidation.cvv ? 'recurly-element-invalid' : undefined}
                style={{ placeholder: { content: 'CVV', color: '#CFCFDF', fontSize: inputFontSize }, fontSize: inputFontSize }}
              />
            </Column>
          </Columns>
        </Rows>
      </Collapse>

      {recurlyErrors &&
        <FormGroup.HelperText className={styles.formError} data-testid="billing-validation-error">{recurlyErrors}</FormGroup.HelperText>}
    </>
  )
}

export default PaymentMethods
